import { FC, useEffect, useRef } from 'react';

import { Avatar, Flex, Group, Stack, StackProps, TitleProps, VisuallyHidden } from '@mantine/core';
import { useInViewport } from '@mantine/hooks';

import { SectionTitle } from 'components/section/SectionTitle';

import cx from './Section.module.scss';

export type SectionProps = {
    id?: string;
    title?: React.ReactNode;
    hiddenTitle?: string;
    titleIcon?: React.ReactNode;
    titleSection?: React.ReactNode;
    hugeTitle?: boolean;
    smallTitle?: boolean;
    titleProps?: TitleProps;
};

const Section: FC<
    Omit<StackProps, 'title'> &
        SectionProps & {
            children: React.ReactNode;
        }
> = ({ id, title, hiddenTitle, titleIcon, titleSection, hugeTitle, smallTitle, titleProps, children, ...props }) => {
    const { ref, inViewport } = useInViewport();
    const hasRendered = useRef(false);

    useEffect(() => {
        if (inViewport) {
            hasRendered.current = true;
        }
    }, [inViewport]);

    return (
        <Stack
            gap="xs"
            data-section
            ref={ref}
            className={`${cx.section}  ${inViewport || hasRendered.current ? cx.visible : ''} ${hugeTitle ? cx.hugeTitle : ''}`}
            id={id}
            {...props}
        >
            {title ? (
                <Stack align="center" justify="center" gap="xs" data-section-title-block>
                    {titleIcon && <Avatar color="brand">{titleIcon}</Avatar>}
                    <SectionTitle hugeTitle={hugeTitle} smallTitle={smallTitle} {...titleProps}>
                        {title}
                    </SectionTitle>

                    {titleSection && (
                        <Group gap="xs" mb="xl">
                            {titleSection}
                        </Group>
                    )}
                </Stack>
            ) : hiddenTitle ? (
                <VisuallyHidden>
                    <SectionTitle>{hiddenTitle}</SectionTitle>
                </VisuallyHidden>
            ) : null}

            {children}
        </Stack>
    );
};

export { Section };
